/* eslint-disable prefer-destructuring */
import React, { useEffect, useRef } from "react";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./SinglePriceCard.module.css";
import classNames from "classnames/bind";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";

const cx = classNames.bind(styles);

const SinglePriceCard = ({ data, cardItemBackgroundColor }) => {
    const { title, subtitle, eyebrowText, description, body } = data ?? {};
    const { currency } = useAppContext();

    // Create new ref for structural adjustments down the line
    const sectionRef = useRef(false);
    const bottomDivRef = useRef(false);
    const noAnchorDivRef = useRef(false);

    // Split the "subtitle" values into an array to get the different prices per currency
    const splitSubtitleArray = subtitle?.split("|").map((item) => item.trim());

    // Initialize variables for the price and plan summary
    let initPlanPrice = "";
    let planSummary = "";

    // When the subtitle has a length of 3
    if (splitSubtitleArray?.length === 3) {
        // The first item is the price, formatted like so: "$825:€825:£725"
        initPlanPrice = splitSubtitleArray?.[0];
        // The third value is the plan sub-text, eg: "20 Full Users minimum, billed annually"
        planSummary = splitSubtitleArray?.[2];
    }

    // Derive color class names for the card from the provided cardItemBackgroundColor
    const { cardColorMapping } = getColorClassName("", cardItemBackgroundColor);

    // Set the class names for the card, including toggles based on the presence of an eyebrow text
    const elClassName = cx({
        singlePriceCard: true,
        relative: true,
        hasEyebrow: eyebrowText,
        "mt-7.5 lg:mt-0 px-7.5 py-7.75 rounded-b bg-white shadow-xxxlLight": eyebrowText,
        "px-7.25 lg:px-5 py-7.5 rounded border border-gainsboro-light": !eyebrowText,
        "mt-8": eyebrowText,
        [cardColorMapping.bgColor]: true,
        [cardColorMapping.primaryTextColor]: true,
    });

    // Set the class names for the description, including toggles based on the presence of an eyebrow text
    const descriptionClassName = cx({
        planDescription: true,
        "tag-3 mb-5 text-sm tracking-normal": true,
        "text-gray": !eyebrowText,
    });

    // PriceEl component to render the price based on the selected currency
    const PriceEl = () => {
        // Split the price string into an array to get the different prices per currency
        const planPricesArray = initPlanPrice.split(":").map((item) => item.trim());

        // Format the price based on the selected currency
        const formattedPlanPrice =
            currency && planPricesArray[currency.id] && planPricesArray[currency.id] !== ""
                ? planPricesArray[currency.id]
                : planPricesArray[0];

        return (
            <>
                {formattedPlanPrice}
                <span className="title-4">
                    {splitSubtitleArray.length === 3 ? splitSubtitleArray[1] : splitSubtitleArray[0]}
                </span>
            </>
        );
    };

    // Function which adjusts the card's body and structure to accommodate the FE layout and grid
    const removeEmptyParagraphs = () => {
        // Remove all empty paragraph tags from the middle body
        const emptyParagraphs = sectionRef.current.querySelectorAll(".singlePriceCard .cardBodyMiddle > p:empty");
        emptyParagraphs.forEach((el) => el.parentNode.removeChild(el));
    };

    useEffect(() => {
        // Remove empty paragraphs from the middle body
        removeEmptyParagraphs();

        return () => {};
    }, [sectionRef, bottomDivRef]);

    return (
        <div className={`${elClassName} singlePriceCard`} ref={sectionRef}>
            {eyebrowText && (
                <div
                    className={`${cx(
                        "isEyebrow"
                    )} absolute bottom-full left-0 right-0 tag-1 px-3.25 py-2.5 rounded-t text-center text-white bg-plum uppercase`}
                >
                    {eyebrowText}
                </div>
            )}
            <div className={`${cx("pricingCardBodyWrapper")} pricingCardBodyWrapper`}>
                <div className={`${cx("cardBodyTop")} top cardBodyTop`}>
                    {title && (
                        <h2
                            className={`${styles.title} title-3 mb-2.5 text-ocean`}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                        />
                    )}
                    {description?.description && <p className={descriptionClassName}>{description.description}</p>}
                    {!(subtitle?.trim() === "") && (
                        <div className={`${cx("cardPricing")} card cardPricing`}>
                            {splitSubtitleArray && (
                                <h3 className="planPrice title-2 mb-2.5 text-blue" suppressHydrationWarning={true}>
                                    {splitSubtitleArray.length === 3 ? (
                                        // If the subtitle has a length of 3, it's a price component
                                        <PriceEl />
                                    ) : (
                                        // Else it's a string like "Get a quote"
                                        <span className="title-3">{splitSubtitleArray[0]}</span>
                                    )}
                                </h3>
                            )}
                            {planSummary ? (
                                <p
                                    className="planSubText tag-3 pb-4.5 text-sm tracking-normal"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(planSummary) }}
                                />
                            ) : (
                                <div className="planSubText pb-4.5"></div>
                            )}
                        </div>
                    )}
                </div>
                <div className={`relative ${cx("cardBodyMiddle")} cardBodyMiddle`}>
                    {renderRichText(body, formatContentfulRichText())}
                    <div className={`${cx("noAnchorParagraphs")} noAnchorParagraphs`} ref={noAnchorDivRef}></div>
                </div>
                <div className={`${cx("cardBodyBottom")} cardBodyBottom nonParagraphChildren`} ref={bottomDivRef}></div>
            </div>
        </div>
    );
};
export default SinglePriceCard;
